import React, { useState } from 'react';
import { Car, X, Save, Search as SearchIcon } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';
import ScanAutopapieren from './ScanAutopapieren';

const BRANDSTOFFEN = ['Benzine', 'Diesel', 'Hybride', 'Elektrisch', 'LPG', 'CNG', 'N.v.t.'];

export const VOERTUIG_TYPES = [
  { v: 'Personenauto', label: 'Personenauto', icon: '🚗' },
  { v: 'Bestelbus', label: 'Bestelbus', icon: '🚐' },
  { v: 'Vrachtwagen', label: 'Vrachtwagen', icon: '🚚' },
  { v: 'Motor', label: 'Motor', icon: '🏍️' },
  { v: 'Scooter', label: 'Scooter / Brommer', icon: '🛵' },
  { v: 'Aanhanger', label: 'Aanhanger', icon: '🛻' },
  { v: 'Caravan', label: 'Caravan / Camper', icon: '🚙' },
  { v: 'Oldtimer', label: 'Oldtimer', icon: '🏎️' },
  { v: 'Anders', label: 'Anders', icon: '🔧' },
];

export const voertuigTypeMeta = (v) => VOERTUIG_TYPES.find(t => t.v === v) || VOERTUIG_TYPES[0];

export default function AutoEditor({ auto, klantId, onClose }) {
  const { t } = useT();
  const { createKlantAuto, updateKlantAuto, rdwLookup, vinLookup } = useData();
  const isExisting = !!(auto && auto._existing);
  const [data, setData] = useState(() => isExisting ? { ...auto } : {
    kenteken: '', klantId: klantId || (auto?.klantId) || '',
    voertuigType: 'Personenauto',
    merk: '', model: '', uitvoering: '', bouwjaar: '', brandstof: 'Benzine', kmStand: 0, apkVervalt: '',
    chassisnummer: '',
    onderhoudIntervalMaanden: 12, onderhoudIntervalKm: 15000, laatsteOnderhoud: '', laatsteOnderhoudKm: 0,
  });
  const upd = (k, v) => setData(d => ({ ...d, [k]: v }));

  const applyScan = (r) => {
    setData(d => ({
      ...d,
      kenteken: d.kenteken || (r.kenteken || ''),
      voertuigType: r.voertuigType && VOERTUIG_TYPES.some(v => v.v === r.voertuigType) ? r.voertuigType : d.voertuigType,
      merk: r.merk || d.merk,
      model: r.model || d.model,
      uitvoering: r.uitvoering || d.uitvoering,
      bouwjaar: r.bouwjaar || d.bouwjaar,
      brandstof: BRANDSTOFFEN.includes(r.brandstof) ? r.brandstof : d.brandstof,
      chassisnummer: r.chassisnummer || d.chassisnummer,
    }));
  };

  const handleRdw = async () => {
    if (!data.kenteken) { toast.error(t('Vul eerst het kenteken in')); return; }
    try {
      const info = await rdwLookup(data.kenteken);
      const apk = info.vervaldatumApk || '';
      const apkFmt = apk && apk.length === 8 ? `${apk.slice(0,4)}-${apk.slice(4,6)}-${apk.slice(6,8)}` : apk;
      const bouwjaar = info.datumEersteToelating ? parseInt(String(info.datumEersteToelating).slice(0, 4)) : data.bouwjaar;
      // RDW voertuigsoort mappen
      const vs = (info.voertuigsoort || '').toLowerCase();
      let detected = data.voertuigType;
      if (vs.includes('bromfiets') || vs.includes('snorfiets')) detected = 'Scooter';
      else if (vs.includes('motor')) detected = 'Motor';
      else if (vs.includes('aanhang') || vs.includes('oplegger')) detected = 'Aanhanger';
      else if (vs.includes('bedrijfs') || vs.includes('bestel')) detected = 'Bestelbus';
      else if (vs.includes('vracht')) detected = 'Vrachtwagen';
      else if (vs.includes('personen')) detected = 'Personenauto';
      setData(d => ({
        ...d,
        merk: info.merk || d.merk,
        model: info.handelsbenaming || d.model,
        bouwjaar: bouwjaar || d.bouwjaar,
        apkVervalt: apkFmt || d.apkVervalt,
        voertuigType: detected,
      }));
      toast.success(`RDW: ${info.merk} ${info.handelsbenaming}`, { description: `${info.voertuigsoort || ''} · ${t('Kleur', 'Kleur')}: ${info.kleur} · APK: ${apkFmt || '-'}` });
    } catch (e) { toast.error(t('RDW lookup mislukt'), { description: e.response?.data?.detail || e.message }); }
  };

  const handleVin = async () => {
    const vin = (data.chassisnummer || '').trim();
    if (!vin) { toast.error(t('Vul eerst het chassisnummer in', 'Vul eerst het chassisnummer in')); return; }
    try {
      const info = await vinLookup(vin);
      setData(d => ({
        ...d,
        merk: info.merk || d.merk,
        model: info.model || d.model,
        uitvoering: info.uitvoering || d.uitvoering,
        bouwjaar: info.bouwjaar || d.bouwjaar,
        brandstof: info.brandstof || d.brandstof,
      }));
      toast.success(`VIN: ${info.merk} ${info.model}`.trim(), { description: [info.bouwjaar, info.brandstofRaw, info.carrosserie, info.productieland].filter(Boolean).join(' · ') });
    } catch (e) { toast.error(t('VIN lookup mislukt', 'VIN lookup mislukt'), { description: e.response?.data?.detail || e.message }); }
  };

  const handleSave = () => {
    const kt = (data.kenteken || '').toUpperCase().trim();
    if (!kt) { toast.error(t('Kenteken is verplicht')); return; }
    if (!data.klantId) { toast.error(t('Geen klant gekoppeld')); return; }
    const payload = { ...data, kenteken: kt, bouwjaar: parseInt(data.bouwjaar) || 0, kmStand: parseInt(data.kmStand) || 0 };
    const op = isExisting ? updateKlantAuto(auto.kenteken, payload) : createKlantAuto(payload);
    op.then(() => { toast.success(t('Voertuig opgeslagen')); onClose(); })
      .catch(e => toast.error(t('Opslaan mislukt'), { description: e.response?.data?.detail || e.message }));
  };

  const typeMeta = voertuigTypeMeta(data.voertuigType);

  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} data-testid="auto-editor">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>
            <span style={{ fontSize: 22, marginRight: 4 }}>{typeMeta.icon}</span>
            {isExisting ? `${t(typeMeta.label, typeMeta.label)} ${auto.kenteken} ${t('bewerken', 'bewerken')}` : t('Nieuw voertuig', 'Nieuw voertuig')}
          </h3>
          <button className="cs-btn ghost" onClick={onClose}><X size={14} /></button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 12, padding: '10px 12px', background: 'var(--cs-accent-soft)', borderRadius: 8 }}>
          <span style={{ fontSize: 12.5, color: 'var(--cs-text-muted)' }}>
            {t('Geen (Nederlands) kenteken? Scan de autopapieren — ook buitenlands (bv. Duits) — en de AI vult de gegevens in.', 'Geen (Nederlands) kenteken? Scan de autopapieren — ook buitenlands (bv. Duits) — en de AI vult de gegevens in.')}
          </span>
          <ScanAutopapieren onResult={applyScan} testid="autoeditor-scan" />
        </div>

        <div style={{ marginBottom: 10 }}>
          <label className="cs-section-title">{t('Voertuigtype', 'Voertuigtype')} *</label>
          <div className="mc-vtype-grid" data-testid="vtype-grid">
            {VOERTUIG_TYPES.map(vt => (
              <button
                key={vt.v}
                type="button"
                className={`mc-vtype ${data.voertuigType === vt.v ? 'active' : ''}`}
                onClick={() => upd('voertuigType', vt.v)}
                data-testid={`vtype-${vt.v.toLowerCase()}`}
              >
                <span style={{ fontSize: 20 }}>{vt.icon}</span>
                <span style={{ fontSize: 11.5 }}>{t(vt.label, vt.label)}</span>
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label className="cs-section-title">{t('Kenteken')} *</label>
            <div style={{ display: 'flex', gap: 4 }}>
              <input data-testid="input-kenteken" className="cs-input" value={data.kenteken} disabled={isExisting} onChange={e => upd('kenteken', e.target.value.toUpperCase())} placeholder="12-AB-34" style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }} />
              <button data-testid="btn-rdw" type="button" className="cs-btn" onClick={handleRdw} title={t('Haal merk/model/APK op via RDW', 'Haal merk/model/APK op via RDW')}><SearchIcon size={12} /> RDW</button>
            </div>
          </div>
          <div><label className="cs-section-title">{t('Bouwjaar')}</label><input type="number" className="cs-input" value={data.bouwjaar} onChange={e => upd('bouwjaar', e.target.value)} /></div>
          <div><label className="cs-section-title">{t('Merk')}</label><input className="cs-input" value={data.merk} onChange={e => upd('merk', e.target.value)} /></div>
          <div><label className="cs-section-title">{t('Model / Type', 'Model / Type')}</label><input className="cs-input" value={data.model} onChange={e => upd('model', e.target.value)} /></div>
          <div style={{ gridColumn: '1 / 3' }}>
            <label className="cs-section-title">{t('Chassisnummer (VIN)', 'Chassisnummer (VIN)')} <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--cs-text-muted)' }}>— {t('handig bij buitenlandse auto\'s', 'handig bij buitenlandse auto\'s')}</span></label>
            <div style={{ display: 'flex', gap: 4 }}>
              <input data-testid="input-chassisnummer" className="cs-input" value={data.chassisnummer || ''} onChange={e => upd('chassisnummer', e.target.value.toUpperCase())} placeholder="WVWZZZ1JZXW000001" maxLength={17} style={{ fontFamily: 'ui-monospace, monospace' }} />
              <button data-testid="btn-vin" type="button" className="cs-btn" onClick={handleVin} title={t('Haal merk/model/bouwjaar op via chassisnummer', 'Haal merk/model/bouwjaar op via chassisnummer')}><SearchIcon size={12} /> VIN</button>
            </div>
          </div>
          <div style={{ gridColumn: '1 / 3' }}><label className="cs-section-title">{t('Uitvoering', 'Uitvoering')}</label><input className="cs-input" value={data.uitvoering} onChange={e => upd('uitvoering', e.target.value)} /></div>
          <div><label className="cs-section-title">{t('Brandstof')}</label><select className="cs-input" value={data.brandstof} onChange={e => upd('brandstof', e.target.value)}>{BRANDSTOFFEN.map(b => <option key={b} value={b}>{t(b, b)}</option>)}</select></div>
          <div><label className="cs-section-title">{t('KM stand', 'KM stand')}</label><input type="number" className="cs-input" value={data.kmStand} onChange={e => upd('kmStand', e.target.value)} /></div>
          <div style={{ gridColumn: '1 / 3' }}><label className="cs-section-title">{t('APK vervalt', 'APK vervalt')} ({t('niet van toepassing voor aanhanger < 750kg, etc.', 'niet van toepassing voor aanhanger < 750kg, etc.')})</label><input type="date" className="cs-input" value={data.apkVervalt} onChange={e => upd('apkVervalt', e.target.value)} data-testid="input-apk" /></div>
          <div style={{ gridColumn: '1 / 3', marginTop: 4, paddingTop: 10, borderTop: '1px solid var(--cs-border)', fontSize: 12.5, fontWeight: 700, color: 'var(--cs-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('Onderhoudsinterval', 'Onderhoudsinterval')}</div>
          <div><label className="cs-section-title">{t('Interval (maanden)', 'Interval (maanden)')}</label><input type="number" className="cs-input" value={data.onderhoudIntervalMaanden ?? ''} onChange={e => upd('onderhoudIntervalMaanden', parseInt(e.target.value) || 0)} placeholder="12" data-testid="input-onderhoud-maanden" /></div>
          <div><label className="cs-section-title">{t('Interval (km)', 'Interval (km)')}</label><input type="number" className="cs-input" value={data.onderhoudIntervalKm ?? ''} onChange={e => upd('onderhoudIntervalKm', parseInt(e.target.value) || 0)} placeholder="15000" data-testid="input-onderhoud-km" /></div>
          <div><label className="cs-section-title">{t('Laatste onderhoud', 'Laatste onderhoud')}</label><input type="date" className="cs-input" value={data.laatsteOnderhoud || ''} onChange={e => upd('laatsteOnderhoud', e.target.value)} data-testid="input-laatste-onderhoud" /></div>
          <div><label className="cs-section-title">{t('Km bij laatste onderhoud', 'Km bij laatste onderhoud')}</label><input type="number" className="cs-input" value={data.laatsteOnderhoudKm ?? ''} onChange={e => upd('laatsteOnderhoudKm', parseInt(e.target.value) || 0)} placeholder="0" data-testid="input-laatste-onderhoud-km" /></div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button className="cs-btn" type="button" onClick={onClose}>{t('Annuleren')}</button>
          <button data-testid="btn-save-auto" className="cs-btn primary" type="button" onClick={handleSave}><Save size={14} /> {t('Opslaan')}</button>
        </div>
      </div>
      <style>{`
        .cs-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.55); display: flex; align-items: center; justify-content: center; z-index: 100; }
        .cs-modal { background: #fff; border-radius: 10px; padding: 20px 22px; width: 620px; max-width: 92vw; max-height: 92vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,0.25); }
        .mc-vtype-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px; }
        .mc-vtype {
          display: flex; flex-direction: column; align-items: center; gap: 4px;
          padding: 8px 4px; border: 1.5px solid var(--cs-border); background: #fff;
          border-radius: 8px; cursor: pointer; transition: all 120ms;
        }
        .mc-vtype:hover { border-color: var(--cs-accent); background: var(--cs-card-alt); }
        .mc-vtype.active { border-color: var(--cs-accent); background: var(--cs-accent-soft); color: var(--cs-accent); font-weight: 600; }
        @media (max-width: 600px) { .mc-vtype-grid { grid-template-columns: repeat(3, 1fr); } }
      `}</style>
    </div>
  );
}
